/* istanbul ignore file */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, ButtonGroup, Flex, Heading, HStack, Select } from '@chakra-ui/react';
import { FormType } from '@common/entities';
import { useQuery } from '@tanstack/react-query';
import { EntrataSession } from 'auth/EntrataSession';
import { VIEWER_URL } from 'config/URL';
import { RootState } from 'state/store';

import { PencilIcon } from 'components/custom-icons';
import { getApplication, getApplicationLocales } from 'services/application';

interface PreviewResponsiveWrapperProps {
  formType: FormType;
}

export function PreviewResponsiveWrapper({ formType }: PreviewResponsiveWrapperProps) {
  const [iframeSize, setIframeSize] = useState('100%');
  const [activeButton, setActiveButton] = useState('desktop');

  const name = useSelector((state: RootState) => state.application.selectedRoute?.name);

  const { applicationId, routeId, versionId } = useParams();
  const navigate = useNavigate();

  const isRouteForm = formType === FormType.Route;

  const { data: applicationData } = useQuery({
    queryKey: ['application', applicationId, versionId],
    queryFn: () => getApplication(applicationId!, versionId!),
    enabled: !isRouteForm && !!applicationId && !!versionId,
  });

  const applicationName = isRouteForm ? name : applicationData?.data?.name;

  const editUrl = isRouteForm
    ? `/routes/route/${routeId}/${versionId}`
    : `/application/form-builder/${applicationId}/${versionId}`;

  const previewUrlBase = isRouteForm
    ? `${VIEWER_URL}/previewroute/${routeId}/${versionId}`
    : `${VIEWER_URL}/preview/${versionId}?user=${encodeURIComponent(JSON.stringify(EntrataSession.user))}`;

  const previewTargetId = (isRouteForm ? routeId : applicationId) as string | undefined;

  const { data: localesResponse } = useQuery({
    queryKey: ['previewIframeLocales', previewTargetId],
    queryFn: () => getApplicationLocales(previewTargetId!),
    enabled: !!previewTargetId,
  });

  const locales = useMemo(() => localesResponse?.data ?? [], [localesResponse]);
  const [previewLocale, setPreviewLocale] = useState('en-US');
  const didInitPreviewLocale = useRef(false);

  useEffect(() => {
    didInitPreviewLocale.current = false;
    setPreviewLocale('en-US');
  }, [previewTargetId]);

  useEffect(() => {
    if (didInitPreviewLocale.current || locales.length === 0) return;
    didInitPreviewLocale.current = true;
    const defaultLocale = locales.find((l) => l.isDefault) ?? locales[0];
    setPreviewLocale(defaultLocale.code);
  }, [locales]);

  const showLanguageSelect = locales.length >= 2;

  const previewUrl = `${
    previewUrlBase + (previewUrlBase.includes('?') ? '&' : '?')
  }locale=${encodeURIComponent(previewLocale)}`;

  const handleSizeChange = (size: string, button: string) => {
    setIframeSize(size);
    setActiveButton(button);
  };

  return (
    <Flex direction="column" bg="gray.highlighthalf" flexBasis="100%" height="calc(100% - 30px)">
      <Flex direction="column" flex="1" bg="gray.highlighthalf" maxW="1800px" w="100%" m="auto">
        <Flex justifyContent="space-between" alignItems="center" mb={2} py={3} px={5}>
          <Heading size="2xl">{applicationName}</Heading>
          <HStack spacing={4}>
            {showLanguageSelect && (
              <Select
                value={previewLocale}
                onChange={(e) => setPreviewLocale(e.target.value)}
                size="sm"
                w="auto"
                minW="200px"
                maxW="280px"
                bg="white.base"
                aria-label="Preview language"
              >
                {locales.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.description}
                    {lang.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </Select>
            )}
            <Button
              leftIcon={<PencilIcon boxSize={5} />}
              onClick={() => navigate(editUrl)}
              variant="secondary"
            >
              Edit
            </Button>
            <Button variant="action" size="lg" onClick={() => navigate(`/`)}>
              Exit
            </Button>
          </HStack>
        </Flex>
        <Box flex="1" display="flex" flexDirection="column" maxW="1700px" m="auto" w="100%">
          <ButtonGroup>
            <Button
              onClick={() => handleSizeChange('100%', 'desktop')}
              as="span"
              role="button"
              bg={activeButton === 'desktop' ? 'white.base' : 'gray.highlight'}
              borderRadius="none"
              borderTopLeftRadius="xl"
              borderTopRightRadius="xl"
              height="unset"
              px={8}
            >
              Desktop
            </Button>
            <Button
              onClick={() => handleSizeChange('390px', 'mobile')}
              as="span"
              role="button"
              bg={activeButton === 'mobile' ? 'white.base' : 'gray.highlight'}
              borderRadius="none"
              borderTopLeftRadius="xl"
              borderTopRightRadius="xl"
              height="unset"
              px={8}
            >
              Mobile
            </Button>
          </ButtonGroup>

          <Box w="100%" bg="white.base" px={3} py={2} h="100%">
            <Box
              w={iframeSize}
              h={activeButton === 'mobile' ? '844px' : '100%'}
              margin="auto"
              borderWidth="1px"
              borderColor={activeButton === 'mobile' ? 'gray.light' : 'white.base'}
              overflow="hidden"
              bg="white.base"
            >
              <iframe
                key={`${applicationId ?? routeId}-${versionId}-${previewUrl}`}
                src={previewUrl}
                width="100%"
                height="100%"
                title="Responsive view"
                style={{ display: 'block' }}
              />
            </Box>
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
}

export default PreviewResponsiveWrapper;
